import { Link } from "react-router-dom";
import { 
  ArrowRight, CheckCircle2, FileText, Calculator, Receipt, Users, 
  Shield, FileCheck, AlertTriangle, Settings, BarChart3, Bell,
  Clock, Download, Eye, Activity, TrendingUp, Bot, ThumbsUp,
  Mail, Percent, Search, ListChecks, Repeat, CreditCard, Palette
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { CTASection } from "@/components/shared/CTASection";
import { SEO } from "@/components/seo/SEO";

const controlItems = [
  "Usinas, unidades consumidoras (UCs) e UGs",
  "Coleta automática de faturas (robôs, e-mail, upload)",
  "Regras de aluguel e rateio por contrato",
  "Auditoria automática de faturas da concessionária",
  "Indicadores ISC e ICC (inteligência de saldo)",
  "Gestão do ciclo de faturamento por status",
  "Área do investidor e área do consumidor",
  "Cobranças, régua de cobrança e status de pagamento",
  "Trilha de auditoria e histórico de ações",
];

const modules = [
  {
    icon: Bot,
    title: "Automação de Faturas",
    subtitle: "Robôs e IA para coleta e processamento",
    description: "Robôs e IA para execução das etapas críticas da entrada de dados. Download e processamento automático via portal da distribuidora, e-mail ou upload manual. Acompanhamento de status e identificação rápida de erros.",
    whyMatters: "Reduz esforço manual e melhora o acompanhamento do processo, eliminando digitação e centralizando todas as faturas.",
    outputs: [
      { icon: Bot, text: "Download automático de faturas" },
      { icon: Mail, text: "Coleta via e-mail" },
      { icon: AlertTriangle, text: "Gestão de status (quarentena/erros)" },
      { icon: Download, text: "Upload manual e exportação" },
    ],
    color: "from-blue-500 to-cyan-500",
  },
  {
    icon: Calculator,
    title: "Aluguel e Rateio",
    subtitle: "Regras parametrizadas e cálculo automático",
    description: "Dezenas de regras de aluguel já cadastradas para diferentes casos de uso. Cálculo automático dos valores. Modelos padrão de fatura com possibilidade de customização visual com sua identidade.",
    whyMatters: "Transforma regras de negócio em processos parametrizados, reduzindo dependência de controles paralelos e aumentando a consistência.",
    outputs: [
      { icon: BarChart3, text: "Detalhamento do aluguel por UC" },
      { icon: FileCheck, text: "Dezenas de regras pré-cadastradas" },
      { icon: Palette, text: "Faturas com identidade visual" },
      { icon: Clock, text: "Histórico de rateios anteriores" },
    ],
    color: "from-violet-500 to-purple-500",
  },
  {
    icon: FileCheck,
    title: "Auditoria de Faturas",
    subtitle: "Validação automática de dados da concessionária",
    description: "Validação automática de dados das faturas da concessionária. A plataforma realiza cálculos, compara e destaca divergências de saldo, injeção e compensado de forma clara.",
    whyMatters: "Agiliza a conferência e fortalece a confiabilidade dos dados. Sua equipe atua de forma mais rápida e fundamentada em casos que exigem revisão junto à concessionária.",
    outputs: [
      { icon: FileCheck, text: "Auditorias automáticas de saldo" },
      { icon: BarChart3, text: "Comparação injeção/compensado" },
      { icon: AlertTriangle, text: "Destaque de divergências" },
      { icon: Search, text: "Apoio a revisões com concessionária" },
    ],
    color: "from-amber-500 to-orange-500",
  },
  {
    icon: Activity,
    title: "Inteligência de Saldo",
    subtitle: "Análise da saúde da carteira",
    description: "Acompanhe saldo de crédito, consumo compensado, inadimplência e histórico de rateio. Identifique oportunidades de redução de estoque de saldo e melhor uso dos créditos.",
    whyMatters: "Permite ação proativa na gestão de créditos, evitando perdas e maximizando o aproveitamento da geração.",
    outputs: [
      { icon: Activity, text: "Histórico de saldo e consumo" },
      { icon: BarChart3, text: "Indicadores ISC e ICC" },
      { icon: TrendingUp, text: "Oportunidades de otimização" },
      { icon: Eye, text: "Faróis coloridos configuráveis" },
    ],
    color: "from-cyan-500 to-teal-500",
  },
  {
    icon: Receipt,
    title: "Gestão Financeira e Cobrança",
    subtitle: "Faturamento, cobrança e conciliação",
    description: "Emissão de boletos, consulta de status, extras e descontos, prorrogação e baixa. Envio de e-mails com faturas e cobranças. Régua de cobrança personalizada e automatizada.",
    whyMatters: "Sua operação ganha mais organização na cobrança e maior previsibilidade sobre seus recebimentos e pendências.",
    outputs: [
      { icon: CreditCard, text: "Emissão de boletos" },
      { icon: Bell, text: "Régua de cobrança automatizada" },
      { icon: Percent, text: "Extras, descontos e prorrogação" },
      { icon: Mail, text: "Envio de faturas por e-mail" },
    ],
    color: "from-emerald-500 to-teal-500",
  },
  {
    icon: ListChecks,
    title: "Gestão do Ciclo de Faturamento",
    subtitle: "Visibilidade sobre cada etapa operacional",
    description: "Gestão por status: cadastro, regras de aluguel, processamento fatura, cálculo aluguel, emissão boleto, pagamento boleto. Filtros avançados por período, cliente, consórcio, geradora. Integração com ERP quando aplicável.",
    whyMatters: "Visão consolidada que reduz dispersão de informação e melhora a capacidade de controle da operação.",
    outputs: [
      { icon: ListChecks, text: "Gestão por status detalhado" },
      { icon: Search, text: "Filtros avançados multi-critério" },
      { icon: Repeat, text: "Integração com ERP" },
      { icon: Eye, text: "Visibilidade do ciclo completo" },
    ],
    color: "from-indigo-500 to-blue-500",
  },
  {
    icon: Users,
    title: "Área do Cliente",
    subtitle: "Portal de autoatendimento para consumidores",
    description: "Ofereça uma experiência mais transparente para seu cliente consumidor: acesso a 2ª via de boletos, status financeiro, histórico de aluguel e economia com GD. Reduz atritos operacionais no atendimento.",
    whyMatters: "Melhora a comunicação com o cliente final e reduz chamados de suporte.",
    outputs: [
      { icon: Download, text: "2ª via de boletos" },
      { icon: TrendingUp, text: "Economia com GD" },
      { icon: Eye, text: "Status financeiro e histórico" },
      { icon: Bell, text: "Notificações por e-mail" },
    ],
    color: "from-pink-500 to-rose-500",
  },
  {
    icon: TrendingUp,
    title: "Área do Investidor",
    subtitle: "Acompanhamento estruturado para investidores",
    description: "Amplie a transparência da operação para os investidores das suas geradoras. Cobranças e boletos vinculados à geradora, histórico de injeção, saldo, consumo e aluguel, status dos boletos e indicadores de rendimento da usina.",
    whyMatters: "Fortalece a comunicação, a transparência e a confiança no relacionamento com seus investidores.",
    outputs: [
      { icon: Receipt, text: "Cobranças por geradora" },
      { icon: BarChart3, text: "Histórico de injeção e saldo" },
      { icon: CreditCard, text: "Status dos boletos" },
      { icon: TrendingUp, text: "Indicadores de rendimento da usina" },
    ],
    color: "from-cyan-500 to-blue-500",
  },
];

const contractFeatures = [
  "Cobrança fixa, variável ou híbrida",
  "Dezenas de regras de aluguel pré-cadastradas",
  "Fórmulas de cálculo complexas e personalizáveis",
  "Regras de priorização entre UCs",
  "Tratamento de excedentes configurável",
  "Vigência e reajustes automáticos",
  "Múltiplos contratos por usina",
];

const auditFeatures = [
  "Registro de todas as ações do sistema",
  "Identificação de usuário e timestamp",
  "Histórico de alterações em contratos",
  "Logs de importação e download de faturas",
  "Rastreabilidade de cobranças e aprovações",
  "Exportação para compliance",
];


export default function GestaoDeCreditos() {
  return (
    <Layout>
      <SEO path="/produto/gestao-de-creditos" title="Gestão de Créditos de Energia — Cogni" description="Controle total do ciclo de créditos de GD: importação de faturas, cálculo de rateio, cobrança e repasse aos beneficiários." />
      {/* HERO */}
      <section className="gradient-hero py-14 sm:py-20 md:py-28 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-60 sm:w-80 h-60 sm:h-80 bg-accent/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
        <div className="container-wide relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4 sm:mb-6">
              Plataforma de Gestão de Créditos
            </span>
            <h1 className="text-display font-bold mb-4 sm:mb-6 text-balance leading-tight">
              A camada de controle da sua operação de créditos de energia
            </h1>
            <p className="text-body-lg text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto">
              Plataforma especializada para estruturar e automatizar o núcleo operacional da gestão de créditos de energia. Da coleta de faturas até a emissão de cobranças e inteligência de saldo.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link to="/contato" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto gradient-primary text-primary-foreground px-8 glow-primary touch-target">
                  Agendar demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* O QUE VOCÊ CONTROLA */}
      <section className="section-padding section-alt">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="text-headline font-bold mb-4">
                O que você controla na Cogni
              </h2>
              <p className="text-body-lg text-muted-foreground">
                Visão centralizada de toda a operação de créditos de energia.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {controlItems.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-2.5 sm:gap-3 bg-background rounded-lg sm:rounded-xl p-3 sm:p-4 border border-border shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
                  </div>
                  <span className="font-medium text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MÓDULOS */}
      <section className="section-padding">
        <div className="container-wide">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-headline font-bold mb-4">
              Módulos da plataforma
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Cada etapa do ciclo de faturamento, do download da fatura até o relacionamento com consumidor e investidor.
            </p>
          </div>

          <div className="space-y-12 sm:space-y-20">
            {modules.map((module, index) => (
              <div 
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-6 sm:gap-8 lg:gap-12 items-start`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2.5 sm:gap-3 mb-3 sm:mb-4">
                    <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br ${module.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
                      <module.icon className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold">{module.title}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">{module.subtitle}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 sm:space-y-4">
                    <div className="bg-muted/30 rounded-lg sm:rounded-xl p-3 sm:p-4 border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base">O que faz</h4>
                      <p className="text-muted-foreground text-sm">{module.description}</p>
                    </div>
                    
                    <div className="bg-accent/30 rounded-lg sm:rounded-xl p-3 sm:p-4 border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base">Por que importa</h4>
                      <p className="text-muted-foreground text-sm">{module.whyMatters}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-foreground mb-2 sm:mb-3 text-sm sm:text-base">O que você vê</h4>
                      <div className="grid grid-cols-1 xs:grid-cols-2 gap-2">
                        {module.outputs.map((output, outputIndex) => (
                          <div key={outputIndex} className="flex items-center gap-2 text-xs sm:text-sm bg-background rounded-lg p-2.5 sm:p-3 border border-border">
                            <output.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                            <span>{output.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 w-full">
                  <div className={`bg-gradient-to-br ${module.color} p-1 rounded-xl sm:rounded-2xl shadow-xl`}>
                    <div className="bg-background rounded-lg sm:rounded-xl p-6 sm:p-8 h-48 sm:h-64 lg:h-80 flex items-center justify-center">
                      <span className="text-muted-foreground text-xs sm:text-sm">[Mockup: {module.title}]</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FLEXIBILIDADE DE CONTRATOS */}
      <section className="section-padding section-gradient-blue relative overflow-hidden">
        <div className="absolute top-0 right-0 w-60 sm:w-80 h-60 sm:h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="container-wide relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <div className="flex items-center gap-2.5 sm:gap-3 mb-3 sm:mb-4">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Settings className="h-5 w-5 sm:h-7 sm:w-7 text-primary" />
                  </div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold">Flexibilidade de contratos</h2>
                </div>
                <p className="text-muted-foreground mb-4 sm:mb-6 text-sm sm:text-base lg:text-lg">
                  Cada usina ou consórcio tem suas próprias regras de cobrança e aluguel. 
                  A Cogni possui dezenas de regras de aluguel pré-cadastradas com fórmulas complexas, 
                  sem precisar de desenvolvimento customizado.
                </p>
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-3">
                  {contractFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs sm:text-sm bg-background/60 rounded-lg p-2.5 sm:p-3 backdrop-blur-sm">
                      <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 h-48 sm:h-64 flex items-center justify-center">
                <span className="text-muted-foreground text-xs sm:text-sm">[Mockup: Configuração de contratos]</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUDITORIA */}
      <section className="section-padding section-dark">
        <div className="container-wide">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div className="order-2 lg:order-1 bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-6 sm:p-8 h-48 sm:h-64 flex items-center justify-center">
                <span className="text-primary-foreground/60 text-xs sm:text-sm">[Mockup: Trilha de auditoria]</span>
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-2.5 sm:gap-3 mb-3 sm:mb-4">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 sm:h-7 sm:w-7 text-primary-foreground" />
                  </div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary-foreground">Auditoria e rastreabilidade</h2>
                </div>
                <p className="text-primary-foreground/80 mb-4 sm:mb-6 text-sm sm:text-base lg:text-lg">
                  Toda ação na plataforma é registrada com identificação de usuário e timestamp. 
                  Essencial para governança, compliance e resolução de disputas com consumidores.
                </p>
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-3">
                  {auditFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs sm:text-sm text-primary-foreground/90 bg-white/5 rounded-lg p-2.5 sm:p-3">
                      <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-400 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <CTASection
        title="Veja a plataforma funcionando"
        subtitle="Agende uma demonstração gratuita e explore a Cogni com dados reais do seu negócio."
      />
    </Layout>
  );
}
